import { useState, useMemo } from 'react';
import { Filter } from '@increasecard/typed-components/dist/types/Filter';
import { useTranslation } from 'react-i18next';
import { useFilterOptions } from '../../common/hooks/useFilterOptions';
import { useShops } from '../../common/hooks/useShops';
import { compareOptionsByName } from '../../utils/filtersUtils';
import { Currency } from '@card/types';
import { useCurrency, useCountry, Country } from '@increasecard/increase-core';
import { useDailyData } from './useDailyData';
import { localizedFullMonthName } from '@card/commonlib';
import { subMonths } from 'date-fns';
import { toCapitalize } from '../../utils/stringUtils';

export type Filters = Record<string, Filter>;

export function useDailyClearingsScreen() {
  const { t } = useTranslation();
  const filterOptions = useFilterOptions();
  const [filters, setFilters] = useState<Filters>({});
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const shops = useShops();
  const defaultCurrency = useCurrency();
  const [currency, setCurrency] = useState<Currency | undefined>(defaultCurrency || undefined);
  const country = useCountry() as Country;

  const lastMonthName = useMemo(() => {
    return toCapitalize(localizedFullMonthName(subMonths(new Date(), 1)));
  }, []);

  const { dailySummary, handleCreateReport, handleCreateReportLastMonth } = useDailyData({
    filters,
    dateRange,
    currency
  });

  return {
    country,
    currencies: filterOptions?.currencies,
    currency,
    setCurrency,
    filters,
    setFilters,
    dateRange,
    setDateRange,
    dailySummary,
    handleCreateReport,
    handleCreateReportLastMonth,
    lastMonthName,
    t,
    filterFields: filterOptions && [
      {
        displayName: t('common:filters:shops:displayName'),
        name: 'shop_id',
        type: 'selectMultiple',
        options: shops
          ? shops.map((shop) => ({ name: shop.name, value: shop.id })).sort(compareOptionsByName)
          : [],
        hasSearch: true,
        hasClearButton: true
      },
      {
        displayName: t('common:filters:bank:displayName'),
        name: 'bank_id',
        type: 'selectMultiple',
        options: filterOptions.banks
          .map(({ id, name }) => ({ value: id, name }))
          .sort(compareOptionsByName),
        hasSearch: true,
        hasClearButton: true
      },
      {
        displayName: t('common:filters:processor:displayName'),
        name: 'info_provider',
        type: 'select',
        options: filterOptions.processorNames
          .map((id) => ({
            name: t(`common:processorNames:${id}`),
            value: id
          }))
          .sort(compareOptionsByName)
      },
      {
        displayName: t('common:filters:cardBrand:displayName'),
        name: 'provider',
        type: 'selectMultiple',
        options: filterOptions.cardBrands
          .map((id) => ({
            name: t(`common:cardBrands:${id}`),
            value: id
          }))
          .sort(compareOptionsByName)
      }
    ]
  };
}
